// Fase 15A — Inviti utente (punti 50-55): creazione, reinvio (nuovo token,
// precedente revocato), revoca. Token mai persistito in chiaro.
import { useCallback, useEffect, useState } from "react";
import { CalendarRange, Plus, Copy, RefreshCw, Ban } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const INV_STYLE = {
  PENDING: "bg-sky-50 text-sky-800 border-sky-300",
  ACCEPTED: "bg-green-50 text-green-800 border-green-300",
  EXPIRED: "bg-amber-50 text-amber-800 border-amber-300",
  REVOKED: "bg-slate-100 text-slate-500 border-slate-300",
};
const ROLES = ["ADMIN_AZIENDA", "ESG_MANAGER", "HR_MANAGER", "DATA_ENTRY",
  "VIEWER", "REVISORE", "ENVIRONMENTAL_MANAGER", "GOVERNANCE_MANAGER"];

export default function PlatformInvitations() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({ email: "", full_name: "", role: "ADMIN_AZIENDA", company_id: "" });
  const [lastLink, setLastLink] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [i, c] = await Promise.all([
        api.get("/admin/platform/invitations",
          { params: { status: status || undefined } }),
        api.get("/admin/platform/companies", { params: { limit: 200 } }),
      ]);
      setData(i.data);
      setCompanies(c.data.items || []);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const showLink = (link) => {
    const full = `${window.location.origin}${link}`;
    setLastLink(full);
    toast.success("Link di attivazione pronto", {
      description: "Copialo e invialo all'invitato: imposterà la propria password." });
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data: r } = await api.post("/admin/platform/invitations",
        { ...form, company_id: form.company_id || null });
      showLink(r.activation_link);
      setForm({ email: "", full_name: "", role: "ADMIN_AZIENDA", company_id: "" });
      await load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const resend = async (inv) => {
    try {
      const { data: r } = await api.post(`/admin/platform/invitations/${inv.id}/resend`);
      showLink(r.activation_link);
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  const revoke = async (inv) => {
    try {
      await api.post(`/admin/platform/invitations/${inv.id}/revoke`);
      toast.success("Invito revocato");
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  if (error) return <p className="text-sm text-red-700" data-testid="invitations-error">{error}</p>;
  if (!data) return <p className="text-sm text-slate-600" data-testid="invitations-loading">Caricamento…</p>;

  return (
    <div className="space-y-6" data-testid="platform-invitations-page">
      <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
        <CalendarRange className="w-7 h-7 text-green-800" aria-hidden="true" /> Inviti
      </h1>

      <Card className="border-slate-200" data-testid="invitation-create-card">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
          <Plus className="w-4 h-4" aria-hidden="true" /> Nuovo invito utente azienda
        </CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="space-y-1">
              <Label htmlFor="inv-name">Nome e cognome *</Label>
              <Input id="inv-name" required value={form.full_name} data-testid="invitation-name-input"
                onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="inv-email">Email *</Label>
              <Input id="inv-email" type="email" required value={form.email} data-testid="invitation-email-input"
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Ruolo</Label>
              <Select value={form.role}
                onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="invitation-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Azienda *</Label>
              <Select value={form.company_id}
                onValueChange={(v) => setForm((f) => ({ ...f, company_id: v }))}>
                <SelectTrigger data-testid="invitation-company-select">
                  <SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.denominazione}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button type="submit" className="bg-green-700 hover:bg-green-800"
                disabled={!form.email || !form.full_name || !form.company_id}
                data-testid="invitation-create-submit">Crea invito</Button>
            </div>
          </form>
          {lastLink && (
            <div className="flex items-center gap-2 rounded-md bg-green-50 border border-green-200 p-2"
              data-testid="invitation-last-link">
              <code className="text-xs text-green-900 truncate flex-1">{lastLink}</code>
              <Button size="sm" variant="outline" className="h-7"
                data-testid="invitation-copy-link"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(lastLink);
                    toast.success("Link copiato");
                  } catch {
                    toast.info("Copia manualmente il link selezionandolo");
                  }
                }}>
                <Copy className="w-3 h-3 mr-1" aria-hidden="true" /> Copia
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex gap-2">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44 h-9" data-testid="invitations-status-filter">
            <SelectValue placeholder="Tutti gli stati" /></SelectTrigger>
          <SelectContent>
            {["PENDING", "ACCEPTED", "EXPIRED", "REVOKED"].map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2" data-testid="invitations-list">
        {data.items.length === 0 && (
          <p className="text-sm text-slate-500">Nessun invito in questo stato.</p>)}
        {data.items.map((i) => (
          <div key={i.id} className="rounded-lg border border-slate-200 p-3 flex flex-wrap items-center gap-3"
            data-testid={`invitation-${i.id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-900">{i.full_name}</p>
              <p className="text-[11px] text-slate-500">
                {i.email} · {i.role} · scade {i.expires_at?.slice(0, 10)}</p>
            </div>
            <Badge variant="outline" className={INV_STYLE[i.status]}
              data-testid={`invitation-status-${i.id}`}>{i.status}</Badge>
            {(i.status === "PENDING" || i.status === "EXPIRED") && (
              <Button size="sm" variant="outline" className="h-7 text-xs"
                onClick={() => resend(i)} data-testid={`invitation-resend-${i.id}`}>
                <RefreshCw className="w-3 h-3 mr-1" aria-hidden="true" /> Reinvia</Button>
            )}
            {i.status === "PENDING" && (
              <Button size="sm" variant="outline" className="h-7 text-xs"
                onClick={() => revoke(i)} data-testid={`invitation-revoke-${i.id}`}>
                <Ban className="w-3 h-3 mr-1" aria-hidden="true" /> Revoca</Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
